import { useEffect, useRef, useState } from 'react'
import { useQuery } from 'react-query'
import classNames from 'classnames'
import colors from '../../config/colors.json'
import { getBackendUrl } from '../../helpers/url'
import { DEFAULT_LANG, getLang } from '../../helpers/i18n'
import { getPbgidIcon, usePbgidIcons } from '../../helpers/pbgidIcons'

const TICKS_PER_SECOND = 8
const MAX_MESSAGES = 8
const FADE_OUT_SECONDS = 0.4

const fetchAbilities = async () => {
	const res = await fetch(`${getBackendUrl()}/api/config/abilities`)
	return res.json()
}

function formatTick(tick) {
	const totalSeconds = Math.max(Math.floor(tick / TICKS_PER_SECOND), 0)
	return `${String(Math.floor(totalSeconds / 60)).padStart(2, '0')}:${String(totalSeconds % 60).padStart(2, '0')}`
}

// Shows the abilities both players used as a single chat log, each message stays for `displayDuration` seconds.
// Only abilities listed in aoe4_abilities.json with a name are shown, the rest (passives, ui helpers) are ignored.
// Using the same ability again while its message is still visible bumps a counter instead of adding a new line.
export function LiveGameAbilityChat({ leftPlayer, rightPlayer, leftName, rightName, leftColor, rightColor, gameTime, displayDuration = 8 }) {
	const { data: abilities } = useQuery('abilities', fetchAbilities, { staleTime: Infinity, cacheTime: Infinity })
	const pbgidIcons = usePbgidIcons()
	const lang = getLang()
	const [messages, setMessages] = useState([])
	const seen = useRef(new Set())
	const lastGameTime = useRef(0)
	const durationMs = Math.max(Number(displayDuration) || 0, 0) * 1000

	useEffect(() => {
		if (!abilities) return

		// a new game started, ticks begin from 0 again
		if (gameTime < lastGameTime.current) {
			seen.current = new Set()
			setMessages([])
		}
		lastGameTime.current = gameTime

		const names = Object.fromEntries(abilities.map((ability) => [ability.pbgid, ability.name?.[lang] || ability.name?.[DEFAULT_LANG]]))
		const sides = [
			{ side: 'left', player: leftPlayer, name: leftName, color: leftColor },
			{ side: 'right', player: rightPlayer, name: rightName, color: rightColor },
		]
		const latestTick = Math.max(gameTime * TICKS_PER_SECOND, ...sides.flatMap(({ player }) => Object.values(player?.abilitiesUsed ?? {}).map((entry) => entry.tick)))

		const incoming = []
		for (const { side, player, name, color } of sides) {
			for (const [key, entry] of Object.entries(player?.abilitiesUsed ?? {})) {
				// keyed on the player name so flipping sides doesn't replay the log
				const id = `${player?.name}-${key}-${entry.tick}`
				if (seen.current.has(id)) continue
				seen.current.add(id)

				const abilityName = names[entry.ability]
				if (!abilityName) continue
				// skip what happened before the overlay was opened
				if (latestTick - entry.tick > (durationMs / 1000) * TICKS_PER_SECOND) continue
				incoming.push({ side, playerName: name, color, ability: Number(entry.ability), abilityName, tick: entry.tick })
			}
		}
		if (!incoming.length || durationMs <= 0) return

		incoming.sort((a, b) => a.tick - b.tick)
		const now = Date.now()
		setMessages((previous) => {
			let updated = previous.filter((message) => message.expiresAt > now)
			for (const message of incoming) {
				const existing = updated.find((m) => m.side === message.side && m.ability === message.ability)
				updated = updated.filter((m) => m !== existing)
				updated.push({
					...message,
					id: `${message.side}-${message.ability}-${message.tick}`,
					count: (existing?.count ?? 0) + 1,
					expiresAt: now + durationMs,
				})
			}
			return updated.slice(-MAX_MESSAGES)
		})
	}, [abilities, leftPlayer, rightPlayer, leftName, rightName, leftColor, rightColor, gameTime, durationMs, lang])

	// remove messages once their display time is up
	useEffect(() => {
		if (!messages.length) return
		const nextExpiry = Math.min(...messages.map((message) => message.expiresAt))
		const timeout = setTimeout(
			() => {
				const now = Date.now()
				setMessages((previous) => previous.filter((message) => message.expiresAt > now))
			},
			Math.max(nextExpiry - Date.now(), 0),
		)
		return () => clearTimeout(timeout)
	}, [messages])

	return (
		<div
			className="liveGameAbilityChat flex flex-col justify-end gap-1"
			style={{ '--ability-chat-duration': `${durationMs / 1000}s`, '--ability-chat-fade': `${FADE_OUT_SECONDS}s` }}>
			{messages.map((message) => {
				const icon = getPbgidIcon(pbgidIcons, message.ability)
				return (
					<div
						key={message.id}
						className={classNames('liveGameAbilityChat__message', `liveGameAbilityChat__message--${message.side}`)}
						style={{ '--ability-chat-color': colors[message.color]?.mainColor }}>
						<span className="liveGameAbilityChat__time">{formatTick(message.tick)}</span>
						<span className="liveGameAbilityChat__player">{message.playerName}</span>
						{icon && <img src={icon} alt="" className="liveGameAbilityChat__icon" />}
						<span className="liveGameAbilityChat__ability">{message.abilityName}</span>
						{message.count > 1 && <span className="liveGameAbilityChat__count">x{message.count}</span>}
					</div>
				)
			})}
		</div>
	)
}
